import { makeObservable, observable, action, computed } from 'mobx'
import axios from 'axios'
import { BaseStore } from './BaseStore'
import type { Tenant } from '@core/models'
import { RootPlan, BetaFreePlan } from '@core/models'
import type { AuthStore } from './AuthStore'

export const PlanString: Record<number, string> = {
  [RootPlan]: 'Root',
  [BetaFreePlan]: 'βフリープラン',
}

export class TenantStore extends BaseStore {
  tenants: Map<string, Tenant> = new Map()
  currentTenant: Tenant | null = null
  authStore: AuthStore | null = null

  constructor(authStore?: AuthStore) {
    super()
    this.authStore = authStore || null
    makeObservable(this, {
      tenants: observable,
      currentTenant: observable,
      authStore: observable,
      getTenants: action,
      getTenant: action,
      createTenant: action,
      updateTenant: action,
      deleteTenant: action,
      tenantsArray: computed,
      nonRootTenants: computed
    })
  }

  async getTenants() {
    const result = await this.handleApiCall(async () => {
      const response = await axios.get<Tenant[]>(
        this.apiPath('v1/tenants'),
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })

    if (result) {
      this.tenants.clear()
      result.forEach(tenant => {
        this.tenants.set(tenant.id, tenant)
      })
      return true
    }
    return false
  }

  async getTenant(tenantId: string) {
    const result = await this.handleApiCall(async () => {
      const response = await axios.get<Tenant>(
        this.apiPath(`v1/tenants/${tenantId}`),
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })

    if (result) {
      this.currentTenant = result
      this.tenants.set(result.id, result)
      // ユーザーの所属テナントの場合、AuthStoreにプランを設定
      if (this.authStore && this.authStore.currentTenantId === result.id) {
        this.authStore.setCurrentTenantPlan(result.plan)
      }
      return true
    }
    return false
  }

  async createTenant(data: Partial<Tenant>) {
    const result = await this.handleApiCall(async () => {
      await axios.post(
        this.apiPath('v1/tenants'),
        data,
        { headers: this.generateFetchHeader() }
      )
      return true
    })

    if (result) {
      // テナント一覧を再取得
      await this.getTenants()
      this.tryShowToast('テナントを作成しました', 'success')
      return true
    }

    this.tryShowToast('テナントの作成に失敗しました', 'error')
    return false
  }

  async updateTenant(tenantId: string, data: Partial<Tenant>) {
    const result = await this.handleApiCall(async () => {
      await axios.put(
        this.apiPath(`v1/tenants/${tenantId}`),
        data,
        { headers: this.generateFetchHeader() }
      )
      return true
    })

    if (result) {
      // 更新されたテナント情報を再取得
      await this.getTenant(tenantId)
      this.tryShowToast('テナント情報を更新しました', 'success')
      return true
    }

    this.tryShowToast('テナント情報の更新に失敗しました', 'error')
    return false
  }

  async deleteTenant(tenantId: string) {
    const result = await this.handleApiCall(async () => {
      await axios.delete(
        this.apiPath(`v1/tenants/${tenantId}`),
        { headers: this.generateFetchHeader() }
      )
      return true
    })

    if (result) {
      this.tenants.delete(tenantId)
      if (this.currentTenant?.id === tenantId) {
        this.currentTenant = null
      }
      this.tryShowToast('テナントを削除しました', 'success')
      return true
    }

    this.tryShowToast('テナントの削除に失敗しました', 'error')
    return false
  }

  get tenantsArray() {
    return Array.from(this.tenants.values())
  }

  get nonRootTenants() {
    return this.tenantsArray.filter(tenant => tenant.plan !== RootPlan)
  }
}